import { DateRange as MUIDateRange } from '@mui/lab/DateRangePicker';
import DateF from './date-f';
import pluralize from '../utils/pluralize';

export type TDateFRangeArray = MUIDateRange<DateF>;
export type TDateRangeArray = MUIDateRange<Date>;
export type TDateRangePlainObject = {
  fromDate: DateF;
  toDate: DateF;
};

class DateRange {
  readonly fromDate: DateF;
  readonly toDate: DateF;

  constructor(dates?: TDateFRangeArray) {
    if (!dates || !dates[0] || !dates[1]) {
      this.fromDate = DateF.startOfYesterday();
      this.toDate = DateF.endOfToday();
      return;
    }

    if (dates[1].isBefore(dates[0])) {
      throw new Error('"to date" must be later than "from date"');
    }

    [this.fromDate, this.toDate] = dates;
  }

  toArray(): TDateRangeArray {
    return [this.fromDate.toVanillaDate(), this.toDate.toVanillaDate()];
  }

  toPlainObject(): TDateRangePlainObject {
    return {
      fromDate: this.fromDate,
      toDate: this.toDate,
    } as TDateRangePlainObject;
  }

  toDBFormat(): Record<string, string> {
    return {
      fromDate: this.fromDate.toISOString(),
      toDate: this.toDate.toISOString(),
    };
  }

  toString(): string {
    return `${this.fromDate?.toLocaleDateString()} - ${this.toDate?.toLocaleDateString()}`;
  }

  get toTimeElapsedPhrase(): string {
    const start = new DateF(this.fromDate.toISOString()).DateTime;
    const complete = new DateF(this.toDate.toISOString()).DateTime;
    const elapsedTime = complete.diff(start, ['days', 'hours', 'minutes', 'seconds']);
    const {
      days, hours, minutes, seconds,
    } = elapsedTime.toObject();

    if (days! > 0 && days! < 7) {
      return `${pluralize(days, 'day')} ago`;
    }
    if (hours! > 0 && hours! < 24) {
      return `${pluralize(hours, 'hour')} ago`;
    }
    if (minutes! > 0 && minutes! < 60) {
      return `${pluralize(minutes, 'minute')} ago`;
    }
    if (seconds! > 0 && seconds! < 60) {
      return '1 minute ago';
    }

    return 'over a week ago';
  }

  static fromVanillaDateArray(dates: TDateRangeArray): DateRange {
    const fromDate = DateF.fromVanillaDate(dates[0] as Date).startOfDay();
    const toDate = DateF.fromVanillaDate(dates[1] as Date).endOfDay();
    return new DateRange([fromDate, toDate]);
  }

  static thisWeek(): DateRange {
    return new DateRange([DateF.startOfThisWeek(), DateF.endOfToday()]);
  }

  static thisMonth(): DateRange {
    return new DateRange([DateF.startOfThisMonth(), DateF.endOfToday()]);
  }

  static thisYear(): DateRange {
    return new DateRange([DateF.startOfThisYear(), DateF.endOfToday()]);
  }

  static lastWeek(): DateRange {
    return new DateRange([DateF.aWeekAgo().startOfDay(), DateF.endOfToday()]);
  }
}

export default DateRange;
